/** PBKDF2-HMAC-SHA256 via Web Crypto — Safari/WebKit-safe AlgorithmIdentifier. */
import { bytesToBase64, base64ToBytes } from './base64.ts'

export const PBKDF2_ITERATIONS = 600_000
/** Backup Android / export web precedenti al bump OWASP. */
export const PBKDF2_ITERATIONS_LEGACY = 310_000
export const PBKDF2_SALT_BYTES = 32
export const PBKDF2_VERIFIER_BITS = 256

export interface MasterPasswordKdfRecord {
  v: 1
  algorithm: 'PBKDF2-HMAC-SHA256'
  iterations: number
  salt: string
  verifier: string
}

export function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false
  let diff = 0
  for (let i = 0; i < a.length; i++) diff |= a[i]! ^ b[i]!
  return diff === 0
}

/** Buffer “puro” per WebKit (copia esplicita, niente SharedArrayBuffer / offset). */
function asWebkitBytes(src: ArrayLike<number> | ArrayBuffer | Uint8Array): Uint8Array<ArrayBuffer> {
  if (src instanceof ArrayBuffer) return Uint8Array.from(new Uint8Array(src))
  return Uint8Array.from(src as ArrayLike<number>)
}

/**
 * Deriva bit PBKDF2. Su Safari:
 * - AlgorithmIdentifier = oggetto `{ name: 'PBKDF2' }`
 * - hash = `{ name: 'SHA-256' }`
 * - password/salt = Uint8Array copiati
 */
export async function derivePbkdf2Sha256(
  passphrase: string,
  salt: BufferSource,
  iterations: number,
  bits = PBKDF2_VERIFIER_BITS,
): Promise<Uint8Array<ArrayBuffer>> {
  const passBytes = asWebkitBytes(new TextEncoder().encode(String(passphrase ?? '')))
  const saltView =
    salt instanceof ArrayBuffer
      ? new Uint8Array(salt)
      : new Uint8Array(salt.buffer, salt.byteOffset, salt.byteLength)
  const saltBytes = asWebkitBytes(saltView)

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    passBytes,
    { name: 'PBKDF2' },
    false,
    ['deriveBits'],
  )

  const derived = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: saltBytes,
      iterations: Math.max(100_000, Math.floor(iterations) || PBKDF2_ITERATIONS),
      hash: { name: 'SHA-256' },
    },
    keyMaterial,
    bits,
  )
  return asWebkitBytes(new Uint8Array(derived))
}

export async function deriveAesGcmKey(
  passphrase: string,
  salt: BufferSource,
  iterations: number,
): Promise<CryptoKey> {
  const bits = await derivePbkdf2Sha256(passphrase, salt, iterations, 256)
  const keyBytes = asWebkitBytes(bits)
  return crypto.subtle.importKey(
    'raw',
    keyBytes,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

export async function createMasterPasswordKdfRecord(
  plainPassword: string,
  iterations = PBKDF2_ITERATIONS,
): Promise<MasterPasswordKdfRecord> {
  const salt = asWebkitBytes(crypto.getRandomValues(new Uint8Array(PBKDF2_SALT_BYTES)))
  const verifier = await derivePbkdf2Sha256(plainPassword, salt, iterations)
  return {
    v: 1,
    algorithm: 'PBKDF2-HMAC-SHA256',
    iterations,
    salt: bytesToBase64(salt),
    verifier: bytesToBase64(verifier),
  }
}

export async function verifyMasterPasswordKdf(
  plainPassword: string,
  record: MasterPasswordKdfRecord,
): Promise<boolean> {
  if (record.v !== 1 || record.algorithm !== 'PBKDF2-HMAC-SHA256') return false
  const salt = asWebkitBytes(base64ToBytes(record.salt))
  const expected = asWebkitBytes(base64ToBytes(record.verifier))
  if (salt.length < 16 || expected.length < 32) return false
  const derived = await derivePbkdf2Sha256(plainPassword, salt, record.iterations)
  return timingSafeEqual(derived, expected)
}

export function serializeMasterPasswordKdf(record: MasterPasswordKdfRecord): string {
  return JSON.stringify(record)
}

export function parseMasterPasswordKdf(raw: string | null | undefined): MasterPasswordKdfRecord | null {
  if (!raw?.trim()) return null
  try {
    const parsed = JSON.parse(raw.trim()) as MasterPasswordKdfRecord
    if (
      parsed?.v === 1 &&
      parsed.algorithm === 'PBKDF2-HMAC-SHA256' &&
      typeof parsed.salt === 'string' &&
      typeof parsed.verifier === 'string' &&
      typeof parsed.iterations === 'number' &&
      parsed.iterations >= 100_000
    ) {
      return parsed
    }
  } catch {
    /* legacy bcrypt string */
  }
  return null
}
